import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { finalize, take } from 'rxjs';
import { LayoutService } from '../../../core/services/layout.service';
import { AppPaths } from '../../../core/router/app-paths';
import { UserSessionService } from '../../../core/services/user-session.service';
import { SidebarModulesService } from '../../../core/services/sidebar-modules.service';
import { OrdersBadgeService } from '../../services/orders-badge.service';
import { PendingRequestsBadgeService } from '../../services/pending-requests-badge.service';
import { TranslatePipe } from '@app/core/i18n/translate.pipe';
import { SidebarAppRole, normalizeSidebarRole } from './sidebar-nav.util';
import type { SidebarNavLink } from './sidebar.models';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
  imports: [RouterLink, RouterLinkActive, TranslatePipe, NgOptimizedImage],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Sidebar {
  protected readonly layout = inject(LayoutService);
  private readonly router = inject(Router);
  private readonly session = inject(UserSessionService);
  private readonly modulesApi = inject(SidebarModulesService);
  private readonly pendingBadge = inject(PendingRequestsBadgeService);
  private readonly ordersNewRecordsBadge = inject(OrdersBadgeService);

  readonly paths = AppPaths;
  readonly isLoggingOut = signal(false);
  readonly status = this.modulesApi.status;
  readonly loadError = this.modulesApi.errorMessage;

  readonly navLinks = computed<SidebarNavLink[]>(() =>
    this.modulesApi.navItems().map((item) => ({
      key: item.key,
      label: item.label,
      labelKey: item.labelKey,
      icon: item.icon,
      path: item.route,
      exact: item.route === AppPaths.dashboard,
      badgeCount:
        item.badge === 'orders'
          ? () => this.ordersNewRecordsBadge.count()
          : item.badge === 'pendingRequests'
            ? () => this.pendingBadge.count()
            : undefined,
      badgeAriaLabelKey:
        item.badge === 'orders'
          ? 'sidebar.badges.newOrdersCount'
          : item.badge === 'pendingRequests'
            ? 'sidebar.badges.pendingCount'
            : undefined,
    })),
  );

  readonly homePath = this.modulesApi.homeRoute;
  readonly profileEnabled = computed(() => this.modulesApi.isRouteAllowed(AppPaths.profile));
  readonly isSuperAdmin = computed(
    () => normalizeSidebarRole(this.session.user()) === SidebarAppRole.SuperAdmin,
  );
  /** Always available to Super Admin, even when the sidebar API omits this tool. */
  readonly showModuleNamesLink = computed(
    () =>
      this.isSuperAdmin() && !this.navLinks().some((link) => link.path === AppPaths.moduleNames),
  );

  readonly userInitials = this.session.userInitials;
  readonly userLogo = computed(() => this.session.user()?.logo?.trim() || null);

  readonly userName = computed(() => {
    const email = this.session.user()?.email?.trim();
    if (!email) return '';
    const local = email.split('@')[0] ?? '';
    const cleaned = local.replace(/[._-]+/g, ' ').trim();
    if (!cleaned) return email;
    return cleaned
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0]!.toUpperCase() + part.slice(1))
      .join(' ');
  });

  readonly userRole = computed(() => this.session.user()?.role?.trim() ?? '');

  constructor() {
    this.modulesApi.ensureLoaded().pipe(take(1)).subscribe();
  }

  retryLoad(): void {
    this.modulesApi.ensureLoaded({ force: true }).pipe(take(1)).subscribe();
  }

  onNavigate(): void {
    if (typeof matchMedia !== 'undefined' && matchMedia('(max-width: 991px)').matches) {
      this.layout.closeMobileSidebar();
    }
  }

  onProfileClick(): void {
    if (!this.profileEnabled()) {
      return;
    }
    void this.router.navigate(['/', AppPaths.profile]);
    this.onNavigate();
  }

  onLogout(): void {
    if (this.isLoggingOut()) return;
    this.isLoggingOut.set(true);

    this.session
      .logout()
      .pipe(finalize(() => this.isLoggingOut.set(false)))
      .subscribe(() => {
        void this.router.navigate(['/', AppPaths.login]).then(() => window.location.reload());
      });
  }
}
